export enum DonorDashboardStep {
    ScreeningQuestions = 0,
    SelectPreferredCenter = 1,
    MedicalHistory = 2,
    PreWorkupLabs = 3,
    ChooseYourCenter = 4,
    TransplantCenterWillContact = 5,
    DonorSatisfactionSurvey = 6,
    Completed = 7
}

export enum DashRegistrationStep {
    GetStarted = 0,
    QuickStart = 1,
    BasicDetails = 5,
    AcceptTransfusions = 10,
    AssessNutcracker = 12,
    AssessLiverHealth = 13,
    AssessOrganDisease = 15,
    AssessHeartAttack = 20,
    AssessDiabetes = 25,
    AssessBloodPressure = 30,
    AssessCancer = 35,
    AssessKidneyStones = 40,
    PregnantOrNursing = 45,
    AssessTobacco = 50,
    AssessHiv = 53,
    DonationDetails = 55,
    DonationDetailsMicrosite = 56,
    VoucherProgram = 57,
    VoucherChoice = 58,
    VoucherChoiceCheck = 59,
    ScreeningSummary = 90,
    ScreeningComplete = 100,
    PreferredCenterSelect = 101,
    HistoryDemographics = 105,
    HistoryDiagnosed = 110,
    HistoryFamily = 115,
    HistoryMisc = 120,
    HistorySocial = 125,
    HistorySummary = 130,
    PreWorkupLabs = 150,
    CystatinCLabs = 155,
    SelectCenter = 195,
    NkrCenters = 198,
    HistoryComplete = 200,
    PendingEmr = 1000,
    PendingContact = 1005,
    PendingWorkup = 1020,
    PendingPreOp = 1025,
    PendingSurgery = 1030,
    SurgeryComplete = 1035,
    Completed = 1500,
    AutoRuledOut = 2000,
    ManuallyRuledOut = 2001,
    TempRuleOut = 2002,
    LocationRuleOut = 2003,
    ExpiredRuleOut = 2004,
    Transferred = 3000,
    Duplicate = 3001,
    ClosedTestRegistration = 3002,
}

interface IScreeningLink {
    step: DashRegistrationStep,
    link: string,
    previousStep?: DashRegistrationStep | null
}

export const screeningLinkMap = (isFemale: boolean, isLiver: boolean): IScreeningLink[] => {
    return [
        { step: DashRegistrationStep.GetStarted, link: '/register/screening/quick-start', previousStep: null },
        { step: DashRegistrationStep.QuickStart, link: '/register/screening/quick-start', previousStep: null },
        { step: DashRegistrationStep.AcceptTransfusions, link: '/register/screening/accept-transfusions', previousStep: DashRegistrationStep.QuickStart },
        { step: DashRegistrationStep.AssessNutcracker, link: '/register/screening/assess-nutcracker', previousStep: DashRegistrationStep.AcceptTransfusions },
        { step: DashRegistrationStep.AssessLiverHealth, link: '/register/screening/assess-liver-health', previousStep: DashRegistrationStep.AcceptTransfusions },
        { step: DashRegistrationStep.AssessOrganDisease, link: '/register/screening/assess-organ-disease', previousStep: !isLiver ? DashRegistrationStep.AssessNutcracker : DashRegistrationStep.AssessLiverHealth },
        { step: DashRegistrationStep.AssessHeartAttack, link: '/register/screening/assess-heart-attack', previousStep: !isLiver ? DashRegistrationStep.AssessOrganDisease : DashRegistrationStep.AssessLiverHealth },
        { step: DashRegistrationStep.AssessDiabetes, link: '/register/screening/assess-diabetes', previousStep: DashRegistrationStep.AssessHeartAttack },
        { step: DashRegistrationStep.AssessCancer, link: '/register/screening/assess-cancer', previousStep: DashRegistrationStep.AssessDiabetes },
        { step: DashRegistrationStep.AssessKidneyStones, link: '/register/screening/assess-kidney-stones', previousStep: DashRegistrationStep.AssessCancer },
        { step: DashRegistrationStep.PregnantOrNursing, link: '/register/screening/pregnancy-or-nursing', previousStep: DashRegistrationStep.AssessKidneyStones },
        { step: DashRegistrationStep.AssessTobacco, link: '/register/screening/assess-tobacco', previousStep: !isFemale ? DashRegistrationStep.AssessKidneyStones : DashRegistrationStep.PregnantOrNursing },
        { step: DashRegistrationStep.AssessHiv, link: '/register/screening/assess-hiv', previousStep: DashRegistrationStep.AssessTobacco },
        { step: DashRegistrationStep.DonationDetails, link: '/register/screening/donation-details', previousStep: DashRegistrationStep.AssessHiv },
        { step: DashRegistrationStep.DonationDetailsMicrosite, link: '/register/screening/donation-details-microsite', previousStep: DashRegistrationStep.AssessHiv },
        { step: DashRegistrationStep.VoucherProgram, link: '/register/screening/voucher-choice', previousStep: DashRegistrationStep.DonationDetails },
        { step: DashRegistrationStep.VoucherChoice, link: '/register/screening/voucher-choice', previousStep: DashRegistrationStep.DonationDetails },
        { step: DashRegistrationStep.VoucherChoiceCheck, link: '/register/screening/voucher-choice-check' },
        { step: DashRegistrationStep.ScreeningSummary, link: '/register/screening/summary', previousStep: DashRegistrationStep.VoucherChoice },
        { step: DashRegistrationStep.ScreeningComplete, link: '/register/screening/complete' },
        { step: DashRegistrationStep.HistoryDemographics, link: '/register/history/demographics' },
    ];
}

export const historyLinkMap: IScreeningLink[] = [
    { step: DashRegistrationStep.HistoryDemographics, link: '/register/history/demographics', previousStep: null },
    { step: DashRegistrationStep.HistoryDiagnosed, link: '/register/history/diagnosed', previousStep: DashRegistrationStep.HistoryDemographics },
    { step: DashRegistrationStep.HistoryFamily, link: '/register/history/family', previousStep: DashRegistrationStep.HistoryDiagnosed },
    { step: DashRegistrationStep.HistoryMisc, link: '/register/history/misc', previousStep: DashRegistrationStep.HistoryFamily },
    { step: DashRegistrationStep.HistorySocial, link: '/register/history/social', previousStep: DashRegistrationStep.HistoryMisc },
    { step: DashRegistrationStep.HistorySummary, link: '/register/history/summary', previousStep: DashRegistrationStep.HistorySocial },
];

export const surveyLinkMap: IScreeningLink[] = [
    { step: DashRegistrationStep.PendingWorkup, link: '/register/pending-workup' },
    { step: DashRegistrationStep.PendingPreOp, link: '/register/pending-preop' },
    { step: DashRegistrationStep.PendingSurgery, link: '/register/pending-surgery' },
    { step: DashRegistrationStep.SurgeryComplete, link: '/register/survey' },
];

export interface IDashboardModel {
    id: number,
    editKey: string,
    centerId: number,
    centerName: string,
    organType: number,
    currentRegistrationStep: DashRegistrationStep,
    isOptedOut: boolean,
    isRuledOut: boolean,
    isAutoRuleOut: boolean,
    isManualRuleOut: boolean,
    offerRuleOutTransfer: boolean,
    ruleOutReasons: string[],
    currentStep: DonorDashboardStep,
    canSelectCenter: boolean,
    canSelectPreferredCenter: boolean,
    useLegacyCenterSelect: boolean,
    hasPwl: boolean,
    isFiveStar: boolean,
    isOptedInForSurveys: boolean,
    offerToConnectToDonor: boolean,
    isNkrDcn: boolean,
    isFemale: boolean,
    isLiver: boolean,
    languageData: Record<string, string>,
}
